// KioskCustomerPickerModal — full-screen touch overlay launched by the
// "New Ticket" card. The operator either taps an existing customer or
// quick-creates one with just a name + type (POST /customers/quick →
// is_complete=false; the rest is filled in on the main module later, which
// is what re-enables ticket completion). On pick/create the modal closes and
// the parent opens the ticket workspace with the chosen customer pre-filled.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './kiosk.module.css';

interface CustomerOption {
  enc_id: string;
  name: string;
  account_status?: 'active' | 'inactive' | 'suspended';
}

interface CustomersListResponse {
  customers: Array<CustomerOption>;
}

type CustomerType = 'residential' | 'commercial';

// Exported so the login prefetch (KioskRoot — S63G) can warm the same cache
// entry this modal's query populates, instead of re-implementing the params
// construction. `search` defaults to '' — the unfiltered quick-pick view.
export async function fetchKioskCustomers(search = ''): Promise<CustomersListResponse> {
  const params: Record<string, string | number> = { limit: 100 };
  if (search !== '') params['search'] = search;
  const res = await apiClient.get<CustomersListResponse>(endpoints.customers.list, { params });
  return res.data;
}

export interface KioskCustomerPickerModalProps {
  readonly onPicked: (customer: { encId: string; name: string; direction: 'inwards' | 'outwards' }) => void;
  readonly onCancel: () => void;
}

export function KioskCustomerPickerModal({
  onPicked,
  onCancel,
}: KioskCustomerPickerModalProps): JSX.Element {
  const [filter, setFilter] = useState('');
  const [debounced, setDebounced] = useState('');
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [direction, setDirection] = useState<'inwards' | 'outwards'>('inwards');

  // Quick-create form.
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<CustomerType>('commercial');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(filter.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [filter]);

  const customersQuery = useQuery<CustomersListResponse>({
    queryKey: queryKeys.customers.list({
      limit: 100,
      ...(debounced !== '' ? { search: debounced } : {}),
    }),
    queryFn: () => fetchKioskCustomers(debounced),
    staleTime: 5 * 60_000, // reference tier — staggered under products' 15min, customer list is more volatile
    enabled: mode === 'search',
  });

  const results = useMemo(() => {
    const all = customersQuery.data?.customers ?? [];
    return all
      .filter(
        (c) => c.account_status === undefined || c.account_status === 'active',
      )
      .slice(0, 100);
  }, [customersQuery.data]);

  async function quickCreate(): Promise<void> {
    const name = newName.trim();
    if (name.length < 2) {
      setCreateError('Enter at least 2 characters for the name.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const res = await apiClient.post<{ customer: CustomerOption }>(
        endpoints.customers.quickCreate,
        { name, customer_type: newType },
      );
      const c = res.data.customer;
      onPicked({ encId: c.enc_id, name: c.name, direction });
    } catch (err) {
      setCreateError(extractErrorMessage(err) ?? 'Could not create the customer.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label="Select customer">
      <div className={styles.modalPanel}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {mode === 'search' ? 'Select customer' : 'New customer'}
          </h2>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onCancel}
            aria-label="Cancel"
          >
            ✕
          </button>
        </div>

        {mode === 'search' ? (
          <>
            <input
              type="search"
              className={styles.searchBar}
              placeholder="Search customers by name…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Search customers"
              data-testid="kiosk-customer-search"
              autoFocus
            />
            <div className={styles.toggleRow} style={{ marginBottom: '0.75rem' }}>
              {(['inwards', 'outwards'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`${styles.toggleCard} ${direction === d ? styles.toggleCardActive : ''}`}
                  onClick={() => setDirection(d)}
                >
                  {d === 'inwards' ? 'Inbound' : 'Outbound'}
                </button>
              ))}
            </div>

            <button
              type="button"
              className={`${styles.ticketGridCard} ${styles.newTicketCard} ${styles.modalNewCustomer}`}
              onClick={() => {
                setNewName(filter.trim());
                setMode('create');
              }}
              data-testid="kiosk-new-customer-card"
            >
              <span className={styles.newTicketPlus}>+</span>
              <span className={styles.newTicketLabel}>New Customer</span>
            </button>

            <div className={styles.modalResults} data-testid="kiosk-customer-results">
              {customersQuery.isLoading ? (
                <p className={styles.muted}>Loading…</p>
              ) : results.length === 0 ? (
                <p className={styles.muted}>No customers match. Tap “New Customer”.</p>
              ) : (
                results.map((c) => (
                  <button
                    key={c.enc_id}
                    type="button"
                    className={styles.modalResultRow}
                    onClick={() => onPicked({ encId: c.enc_id, name: c.name, direction })}
                    data-testid={`kiosk-customer-${c.enc_id}`}
                  >
                    {c.name}
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <div className={styles.modalForm}>
            {createError ? <div className={styles.error}>{createError}</div> : null}
            <div className={styles.field} style={{ maxWidth: '100%' }}>
              <label className={styles.label} htmlFor="kiosk-new-customer-name">
                Customer name
              </label>
              <input
                id="kiosk-new-customer-name"
                className={styles.input}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Acme Skips Ltd"
                aria-label="Customer name"
                autoFocus
              />
            </div>
            <div>
              <label className={styles.label}>Type</label>
              <div className={styles.toggleRow}>
                {(['commercial', 'residential'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`${styles.toggleCard} ${
                      newType === t ? styles.toggleCardActive : ''
                    }`}
                    onClick={() => setNewType(t)}
                  >
                    {t === 'commercial' ? 'Commercial' : 'Residential'}
                  </button>
                ))}
              </div>
            </div>
            <p className={styles.muted}>
              Contact &amp; address details can be added later on the main module.
              The ticket can be weighed now but can’t be closed until the customer
              is completed.
            </p>
            <div className={styles.stepNav}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => setMode('search')}
                disabled={creating}
              >
                ← Back
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => void quickCreate()}
                disabled={creating || newName.trim().length < 2}
                data-testid="kiosk-create-customer-confirm"
              >
                Create &amp; continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
